// src/lib/demoScenes.js — the guided rail: fourteen scenes, each a real OS screen.
//
// docs/specs/DEMO-ENVIRONMENT-v0.1.md §0 "The six scenes". A scene is a path
// into the product plus the one sentence a visitor should be reading the
// screen for. The ids are the deterministic ids scripts/demo/seed.mjs writes
// (refId over the fixture ref — see scripts/demo/ref-id.mjs); they are carried
// here as literals so the bundle needs no hash function, and
// src/lib/demoScenes.test.js asserts they still match the derivation.
//
// Pure: no env, no React. src/contexts/DemoContext.jsx drives it.

export const DEMO_IDS = {
  user:        'a1504021-a45f-5059-8a1b-424dc94e4f80',   // user:nora-meridian
  deal:        '6fd4259f-75b1-5af2-a0f2-15e00f8dd8e6',   // deal:tidewater
  contactDana: '2e556c17-ce57-55c9-b774-086d24943c27',   // contact:dana
  project:     'b0a767a5-8cfb-54af-88e6-a2717f9f915b',   // project:tidewater-series
  property:    'b9bda44f-bd6b-5f25-8850-8f75c8ceb3fc',   // property:tidewater
  latestEmail: '3d40bf71-bb77-55e7-aaa5-7ba72cd1cb8c',   // intel:email:latest
  room:        '99600b0a-64bf-5d04-b992-544112cfb50f',   // console:thread:pilot — the writer's room, in the demo CONSOLE project
  designBrief: 'e4d6c699-6916-573b-a3bc-af22083d88ca',   // design:brief:isaiah — scripts/demo/fixtures/meridian-design.mjs
  gnProject:   'a0ce8f5c-22f0-5d6b-91b6-880a693abae8',   // project:tidewater-gn — the graphic novel expression
  verticalProject: '941e87fe-2e9f-5c5e-b868-7f6a2140bf0a', // project:tidewater-vertical — the vertical series expression
}

// The Eighty Days scenario's own ids (scripts/demo/fixtures/eightyDays.mjs),
// computed from scripts/demo/ref-id.mjs's deterministic refId() the same way
// every id above was — same derivation `node scripts/demo/seed.mjs
// --print-scene-refs` prints, carried as literals for the same reason DEMO_IDS
// is: no hash function in the browser bundle.
//
// DEMO_SCENES below is now built from these — the primary guided rail reads
// the IP's lifecycle (acquisition deal -> development/canon -> graphic novel
// -> vertical series -> TV pilot -> animated series -> live-action feature ->
// distribution) through real canonical records, not the standalone
// /demo/journey fixture page. This is a SOURCE-level wire-up: the ids below
// name rows that do not exist yet in the live demo Supabase project
// (kuenfiasnrygefxnggeb) until scripts/demo/seed.mjs --target eighty-days
// --apply runs as a separate, human-approved step (docs/demo/
// eighty-days-content-manifest.md). Until that apply lands, a visitor to the
// deployed trailer following this rail would 404 past "Morning" — that is a
// deployment/data gate, not a code defect, and is tracked separately from
// this file. DEMO_IDS (Tidewater) stays exported and in use: it is still the
// historical, independently-verified second example linked from DemoRail and
// DemoJourneyPage, and its own records are untouched by this change.
export const EIGHTY_DAYS_IDS = {
  property:         '8ebadb05-8c5f-5221-bfef-04e40c832e26', // property:eighty-days
  propertyClassic:  '34b5a0bb-a895-5a16-bd95-3622593afc2c', // property:eighty-days-classic
  deal:             'c923557b-b022-5fac-b6ce-83d0c6167757', // deal:eighty-days-acquisition
  contactMaraVale:  '5007ba93-9eea-5f17-8cd4-75fa28e59a33', // contact:mara-vale
  companyWayfinder: '42f61f92-64d2-5329-be2b-5979b984efa3', // company:wayfinder-press
  gnProject:        '94e5dc39-7d03-562c-90bf-54334ba2fee3', // project:eighty-days-gn
  verticalProject:  '3c556b61-116f-55aa-a62e-6ae8f34a8340', // project:eighty-days-vertical
  pilotProject:     '0d5848c7-e8ac-5cb3-80a3-42d9ce2b38f7', // project:eighty-days-pilot
  animationProject: '0f5e93cd-bc94-5d5f-bfbb-30df3766f7c1', // project:eighty-days-animation
  featureProject:   '93b4d470-3952-563e-a83b-fd6a0667cfda', // project:eighty-days-feature
  room:             'df95a29a-ff6e-586c-b459-7ba1b2f27299', // console:thread:e80-pilot — the writers' room, in the demo CONSOLE project
}

// The primary rail: the IP's own lifecycle, read through real screens —
// acquisition deal -> development/canon (shared across every format) ->
// graphic novel -> vertical series -> TV pilot -> animated series ->
// live-action feature -> distribution — with Janet as the guide throughout
// (every scene's page sets Janet's ambient context; "morning" and "ask"
// additionally carry a scripted question). Tidewater — the first scenario
// built, still a complete, independently-verified second example — is no
// longer on this primary rail; it stays reachable from DemoRail's and
// DemoJourneyPage's own explicit links, with its records, ids and history
// untouched.
export const DEMO_SCENES = [
  {
    key: 'morning',
    title: 'Morning',
    path: '/',
    lead: 'What needs my attention today?',
    notice: 'Janet reads across the deals, the deadlines, the meetings and the open loops — nobody typed a status report.',
    janet: 'What needs my attention today?',
  },
  {
    key: 'slate',
    title: 'The slate',
    path: '/studio/hub',
    lead: 'Every property in development, and exactly where each version stands.',
    notice: 'Around the World in Eighty Days sits here as five versions of one IP — a graphic novel, a vertical series, a TV pilot, an animated series and a live-action feature — each at its own stage, never one finished show standing in for all five.',
  },
  {
    key: 'deal',
    title: 'The acquisition',
    path: `/deals/${EIGHTY_DAYS_IDS.deal}`,
    lead: 'One signed rights grant, read out of the deal record.',
    notice: 'A fictional master agreement with Mara Vale and Wayfinder Press: signed once, it already carries a schedule for all five formats below. The shared signing and exercise fees, and where the deal stands, are read from this record — nothing here is retyped by hand.',
  },
  {
    key: 'property',
    title: 'The property',
    path: `/properties/${EIGHTY_DAYS_IDS.property}`,
    lead: 'One IP, five exploitations, and what is waiting on you.',
    notice: 'Around the World in Eighty Days as one tile per version. Each carries its own original sample and its own draft distribution checklist, built from the one shared, approved development brief. Click a version to open its writing.',
  },
  {
    key: 'graphic-novel',
    title: 'Graphic novel',
    path: `/properties/${EIGHTY_DAYS_IDS.property}?lens=${EIGHTY_DAYS_IDS.gnProject}&open=writing`,
    lead: 'Developed first: a two-page, nine-panel sample.',
    notice: 'A real canonical draft — panel descriptions and captions only — opened in the same reader every written material uses.',
  },
  {
    key: 'pages',
    title: 'The pages',
    path: `/properties/${EIGHTY_DAYS_IDS.property}?lens=${EIGHTY_DAYS_IDS.gnProject}&open=pages`,
    lead: 'The same nine panels, drawn and lettered.',
    notice: 'Each panel was drawn from the script above against the approved character sheets for Fogg and Passepartout, so they stay on model from page to page. The pictures are AI-generated sample art, never presented as commissioned artwork.',
  },
  {
    key: 'vertical',
    title: 'Vertical series',
    path: `/properties/${EIGHTY_DAYS_IDS.property}?lens=${EIGHTY_DAYS_IDS.verticalProject}&open=writing`,
    lead: 'The same wager, adapted for phones: a 28-second, five-shot cut script.',
    notice: 'A shot-by-shot script with on-screen text, read as a real draft on this version\'s own materials.',
  },
  {
    key: 'episodes',
    title: 'The episode',
    path: `/properties/${EIGHTY_DAYS_IDS.property}?lens=${EIGHTY_DAYS_IDS.verticalProject}&open=episodes`,
    lead: 'Episode 1, cut from its storyboards and playable on a phone.',
    notice: 'The 28-second cut script, shot for shot, over AI-generated 9:16 storyboard frames. Episodes 2–4 are written and listed as scripts. No footage has been filmed.',
  },
  {
    key: 'pilot',
    title: 'TV pilot',
    path: `/properties/${EIGHTY_DAYS_IDS.property}?lens=${EIGHTY_DAYS_IDS.pilotProject}&open=writing`,
    lead: 'The same wager, as a continuing series: a two-page pilot scene.',
    notice: 'An original scene excerpt that sets the series\' engine running — the exact draft that gets Fogg out of London. No table read or footage exists for it yet.',
  },
  {
    key: 'room',
    title: 'The writers\' room',
    path: `/console/thread/${EIGHTY_DAYS_IDS.room}`,
    lead: 'The pilot scene, worked in the room: two collaborators, six reviewers, one decision.',
    notice: 'Nora and two AI collaborators take the station scene apart, the room scores the revised pass, and the decision it reaches is recorded — the same notebook beat now closes the graphic novel, the vertical episode and the pilot. A read-only replay.',
  },
  {
    key: 'promo',
    title: 'The promo',
    path: `/properties/${EIGHTY_DAYS_IDS.property}?lens=${EIGHTY_DAYS_IDS.pilotProject}&open=promo`,
    lead: 'A sizzle for the pilot, cut from the property\'s own pictures.',
    notice: 'Cut 1, Nora\'s notes on it, and cut 2 made from those notes and waiting for her. Every picture is one the graphic novel and the vertical episode already use.',
  },
  {
    key: 'animation',
    title: 'Animated series',
    path: `/properties/${EIGHTY_DAYS_IDS.property}?lens=${EIGHTY_DAYS_IDS.animationProject}&open=writing`,
    lead: 'The same wager, boarded for animation: a three-beat storyboard.',
    notice: 'Written shot and timing notes only — no posing, rigging or rendered animation exists for it yet, and none is claimed here.',
  },
  {
    key: 'feature',
    title: 'Live-action feature',
    path: `/properties/${EIGHTY_DAYS_IDS.property}?lens=${EIGHTY_DAYS_IDS.featureProject}&open=writing`,
    lead: 'The same wager, staged for a feature: one scene and a written shot list.',
    notice: 'A scene excerpt plus a previs shot list — pending an actual previs render. No table read, casting or previs exists for it yet.',
  },
  {
    key: 'ask',
    title: 'Ask Janet anything',
    path: '/janet/chat',
    lead: 'Free-form, live, grounded in the whole company.',
    notice: 'Ask your own question. The answer is generated live against the record you just walked through.',
    janet: 'Across the five Eighty Days formats, which is furthest along, and what still has to happen before any paid artwork or footage gets commissioned?',
  },
]

// Three of the fourteen scenes above read Studio tables (the graphic
// novel's drawn pages, the vertical episode's cut, the pilot's promo cut)
// that this PR deliberately leaves for a separate, human-run step after
// merge: generating + uploading the pictures (scripts/demo/eighty-days-art.mjs)
// and re-applying the seed with them on file (docs/demo/
// eighty-days-content-manifest.md "The pictures are written, not yet
// made."). The seed itself already refuses to write a row that names a
// missing picture — but the guided tour is a SEPARATE promise: it must not
// advertise or route a live visitor to one of these three screens before
// that step has actually run, or a visitor lands on an empty Studio page in
// the middle of what is supposed to be the showpiece of the tour. Until the
// deploy is told the pictures are live, readyScenes() below drops exactly
// these three — nothing else changes; every other scene (including the
// writing tabs for the same three formats, which read plain `documents`
// rows the base seed already writes) stays on the rail unconditionally.
export const STUDIO_GATED_KEYS = new Set(['pages', 'episodes', 'promo'])

/** DEMO_SCENES filtered to what is safe to advertise/route a visitor to
 *  right now. `studioReady` must be an explicit, deliberate signal that the
 *  Eighty Days Studio pictures have been generated, uploaded, and the seed
 *  re-applied with them on file — never inferred, never defaulted true (a
 *  falsy/missing value keeps the three Studio-only scenes off the rail,
 *  which is the safe default for a deploy where nobody has said otherwise).
 *  DEMO_SCENES itself is untouched — this is a view over it, not a rewrite. */
export function readyScenes(studioReady) {
  return studioReady ? DEMO_SCENES : DEMO_SCENES.filter((s) => !STUDIO_GATED_KEYS.has(s.key))
}

// The pages, episode and promo exist only once the Studio pictures are live
// (the same switch that puts those scenes on the guided rail) — until then the
// caption must not say they exist.
export function studioArtSentence(ready) {
  return ready
    ? "The graphic novel's pages, the vertical series' first episode and the TV pilot's promo are drawn from these samples as AI-generated sample art — no commissioned artwork, filmed footage or recorded audio exists for any format."
    : "The graphic novel's pages, the vertical series' first episode and the TV pilot's promo have not been made yet — no artwork, filmed footage or recorded audio exists for any format."
}

// A property's tiles are one pathname with many screens; lens + open say which.
const KEY_PARAMS = ['lens', 'open']
const keyOf = (search) => {
  const q = new URLSearchParams(search || '')
  return KEY_PARAMS.map((k) => q.get(k) || '').join('|')
}

/** Index of the scene whose path matches a location (pathname + search),
 *  within `scenes` (defaults to the full DEMO_SCENES — pass readyScenes()'s
 *  result to search only what is currently safe to route to).
 *  Exact first; then the same screen (pathname + lens/open), so a visitor who
 *  changes a tab or opens a card inside the scene is still "in" it; then a
 *  door the scene points at (`also`); then the pathname alone. -1 when off
 *  the rail. */
export function sceneIndexFor(pathname, search = '', scenes = DEMO_SCENES) {
  const here = `${pathname}${search || ''}`
  const exact = scenes.findIndex((s) => s.path === here)
  if (exact >= 0) return exact
  const key = keyOf(search)
  const same = scenes.findIndex((s) => {
    const [p, q = ''] = s.path.split('?')
    return p === pathname && keyOf(q) === key
  })
  if (same >= 0) return same
  // A version's own tiles (its lens, no tile open) stay in the scene that
  // shows that version, so the breadcrumb out of its Writing tab is still
  // that format's own scene (the graphic novel, the vertical series, …).
  const q = new URLSearchParams(search || '')
  const lens = q.get('lens')
  const lensOnly = lens && !q.get('open') ? scenes.findIndex((s) => {
    const [p, sq = ''] = s.path.split('?')
    return p === pathname && new URLSearchParams(sq).get('lens') === lens
  }) : -1
  if (lensOnly >= 0) return lensOnly
  const door = scenes.findIndex((s) => (s.also || []).some((a) => a.split('?')[0] === pathname))
  if (door >= 0) return door
  return scenes.findIndex((s) => s.path.split('?')[0] === pathname)
}

export function sceneAt(index, scenes = DEMO_SCENES) {
  return scenes[Math.max(0, Math.min(scenes.length - 1, index))]
}
